import axios from 'axios';
import config from '../Config';
import authHeader from './auth-header';
import { handle401Unauthorized } from '../utils/Helper';

const API_URL = config.API_URL;
const API_VERSION = config.API_VERSION;

// Create a separate axios instance for DieSize module to handle 401 errors
const dieSizeAxiosInstance = axios.create();

// Add response interceptor to handle 401 errors for DieSize module
dieSizeAxiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      handle401Unauthorized();
    }
    return Promise.reject(error);
  }
);

// DieSize APIs
const getDieSizeList = (page, pagesize, search, ordering) => {
  return dieSizeAxiosInstance.get(API_URL + '/api/' + API_VERSION + '/die-size/', {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getDieSizeById = id => {
  return dieSizeAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-size/${id}/`, {
    headers: authHeader(),
  });
};

const addDieSize = formData => {
  return dieSizeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-size/`,
    formData,
    { headers: authHeader() },
  );
};

const updateDieSize = (id, formPayload) => {
  return dieSizeAxiosInstance.put(
    `${API_URL}/api/${API_VERSION}/die-size/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const patchDieSize = (id, formPayload) => {
  return dieSizeAxiosInstance.patch(
    `${API_URL}/api/${API_VERSION}/die-size/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const deleteDieSize = id => {
  return dieSizeAxiosInstance.delete(
    `${API_URL}/api/${API_VERSION}/die-size/${id}/`,
    { headers: authHeader() },
  );
};

const getDieSizeDropdown = (filterKey) => {
  return dieSizeAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-size/dropdown/`, {
    headers: authHeader(),
    params: {
      ...(filterKey && { filter_key: filterKey }),
    },
  });
};

const getDieSizeArchiveList = (page, pagesize, search, ordering) => {
  return dieSizeAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-size/archived/`, {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getDieSizeArchiveById = id => {
  return dieSizeAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-size-archive/${id}/`, {
    headers: authHeader(),
  });
};

const restoreDieSize = ids => {
  return dieSizeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-size/bulk-restore/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

const bulkArchiveDieSize = ids => {
  return dieSizeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-size/bulk-archive/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

// Bulk Import APIs
const bulkImportDieSize = formData => {
  return dieSizeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-size/bulk-import/`,
    formData,
    {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

const getImportLogs = (page, pagesize) => {
  return dieSizeAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/die-size/import-logs/`,
    {
      headers: authHeader(),
      params: {
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const getImportErrors = (importLogId, page, pagesize) => {
  return dieSizeAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/die-size/import-errors/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const downloadErrorReport = importLogId => {
  return dieSizeAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/die-size/error-report/download/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
      },
      responseType: 'blob',
    },
  );
};

const dieSizeService = {
  getDieSizeList,
  getDieSizeById,
  addDieSize,
  updateDieSize,
  patchDieSize,
  deleteDieSize,
  getDieSizeDropdown,
  getDieSizeArchiveList,
  getDieSizeArchiveById,
  restoreDieSize,
  bulkArchiveDieSize,
  bulkImportDieSize,
  getImportLogs,
  getImportErrors,
  downloadErrorReport,
};

export default dieSizeService;
